<script setup lang="ts">
import type { ImportColumnMapping } from '../internal/types.ts'
import { ActionButton, FileUpload, Select, StatusTag } from '@taler/ui'
import { computed, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import type { AccountOption } from '../internal/account-options.ts'
import { guessMapping, sniffHeaders } from '../internal/mapping.ts'

const NONE = '__none__'

const props = defineProps<{
  statementOptions: readonly AccountOption[]
  isCreating: boolean
  errorDetail: string | undefined
}>()

const emit = defineEmits<{ submit: [{ accountId: string; file: File; mapping: ImportColumnMapping }] }>()

const { t } = useI18n()

const accountId = ref<string | undefined>(undefined)
const file = ref<File | null>(null)
const headers = ref<readonly string[]>([])

const mapping = reactive<{
  date: string | undefined
  amount: string | undefined
  description: string | undefined
  accountCode: string
  externalReference: string
}>({ date: undefined, amount: undefined, description: undefined, accountCode: NONE, externalReference: NONE })

const statementSelectOptions = computed(() =>
  props.statementOptions.map((option) => ({ value: option.id, label: option.label })),
)
const headerOptions = computed(() => headers.value.map((header) => ({ value: header, label: header })))
const optionalHeaderOptions = computed(() => [
  { value: NONE, label: t('imports.mapping.none') },
  ...headerOptions.value,
])

const canSubmit = computed(
  () =>
    accountId.value !== undefined &&
    file.value !== null &&
    mapping.date !== undefined &&
    mapping.amount !== undefined &&
    mapping.description !== undefined &&
    !props.isCreating,
)

watch(file, async (picked) => {
  if (picked === null) {
    headers.value = []
    return
  }
  headers.value = await sniffHeaders(picked)
  const guess = guessMapping(headers.value)
  mapping.date = guess.date
  mapping.amount = guess.amount
  mapping.description = guess.description
  mapping.accountCode = guess.accountCode ?? NONE
  mapping.externalReference = guess.externalReference ?? NONE
})

function onSubmit(): void {
  if (
    accountId.value === undefined ||
    file.value === null ||
    mapping.date === undefined ||
    mapping.amount === undefined ||
    mapping.description === undefined
  ) {
    return
  }
  emit('submit', {
    accountId: accountId.value,
    file: file.value,
    mapping: {
      date: mapping.date,
      amount: mapping.amount,
      description: mapping.description,
      accountCode: mapping.accountCode === NONE ? undefined : mapping.accountCode,
      externalReference: mapping.externalReference === NONE ? undefined : mapping.externalReference,
    },
  })
}
</script>

<template>
  <form
    class="flex flex-col gap-[var(--space-block)] rounded-[var(--radius-surface)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-[var(--space-block)]"
    @submit.prevent="onSubmit"
  >
    <div class="flex flex-wrap items-end gap-[var(--space-block)]">
      <FileUpload
        v-model="file"
        class="w-72"
        :label="t('imports.upload.file')"
        accept=".csv,text/csv"
        :accept-hint="t('imports.upload.acceptHint')"
        :browse-label="t('imports.upload.browseFile')"
        :drop-hint="t('imports.upload.dropHint')"
        :remove-label="t('imports.upload.removeFile')"
        :invalid-type-message="t('imports.upload.invalidFileType')"
      />

      <label class="flex w-64 flex-col gap-1">
        <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
          t('imports.upload.statementAccount')
        }}</span>
        <Select
          v-model="accountId"
          :options="statementSelectOptions"
          :placeholder="t('imports.upload.selectStatementAccount')"
          :aria-label="t('imports.upload.statementAccount')"
        />
      </label>
    </div>

    <div v-if="headers.length > 0" class="flex flex-col gap-[var(--space-cell)]">
      <p class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">
        {{ t('imports.mapping.title') }}
      </p>
      <p class="text-[color:var(--color-text-muted)]">{{ t('imports.mapping.description') }}</p>
      <div class="grid grid-cols-2 gap-[var(--space-cell)] sm:grid-cols-3">
        <label class="flex flex-col gap-1">
          <span class="text-[length:var(--text-step-2)] text-[color:var(--color-text)]">{{
            t('imports.mapping.fields.date')
          }}</span>
          <Select
            v-model="mapping.date"
            :options="headerOptions"
            :placeholder="t('imports.mapping.placeholders.selectColumn')"
            :aria-label="t('imports.mapping.fields.date')"
          />
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-[length:var(--text-step-2)] text-[color:var(--color-text)]">{{
            t('imports.mapping.fields.amount')
          }}</span>
          <Select
            v-model="mapping.amount"
            :options="headerOptions"
            :placeholder="t('imports.mapping.placeholders.selectColumn')"
            :aria-label="t('imports.mapping.fields.amount')"
          />
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-[length:var(--text-step-2)] text-[color:var(--color-text)]">{{
            t('imports.mapping.fields.description')
          }}</span>
          <Select
            v-model="mapping.description"
            :options="headerOptions"
            :placeholder="t('imports.mapping.placeholders.selectColumn')"
            :aria-label="t('imports.mapping.fields.description')"
          />
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-[length:var(--text-step-2)] text-[color:var(--color-text)]">{{
            t('imports.mapping.fields.accountCode')
          }}</span>
          <Select
            v-model="mapping.accountCode"
            :options="optionalHeaderOptions"
            :aria-label="t('imports.mapping.fields.accountCode')"
          />
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-[length:var(--text-step-2)] text-[color:var(--color-text)]">{{
            t('imports.mapping.fields.externalReference')
          }}</span>
          <Select
            v-model="mapping.externalReference"
            :options="optionalHeaderOptions"
            :aria-label="t('imports.mapping.fields.externalReference')"
          />
        </label>
      </div>
    </div>

    <div>
      <ActionButton
        size="lg"
        type="submit"
        :disabled="!canSubmit"
        class="text-[length:var(--text-step-3)] font-medium"
      >
        {{ isCreating ? t('imports.upload.creating') : t('imports.upload.createJob') }}
      </ActionButton>
    </div>

    <StatusTag v-if="errorDetail !== undefined" tone="danger" role="alert">
      {{ errorDetail }}
    </StatusTag>
  </form>
</template>
